import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Card,
  Tag,
  Modal,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Tooltip,
} from "antd";
import {
  RefreshCw,
  Eye,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import apiService from "../../services/api";
import {
  setAlerts,
  updateAlert,
  setStats,
  setLoading,
  setError,
  selectAlerts,
  selectAlertStats,
  selectAlertLoading,
} from "../../store/alertSlice";
import AlertDetail from "./AlertDetail";
import { formatDateTime } from "../../utils/formatters";

const { Search } = Input;
const { Option } = Select;

const AlertList = () => {
  const dispatch = useDispatch();
  const alerts = useSelector(selectAlerts);
  const stats = useSelector(selectAlertStats);
  const loading = useSelector(selectAlertLoading);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [filters, setFilters] = useState({
    type: "",
    severity: "",
    status: "",
    is_read: null, // null, true, false
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadAlerts = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filters,
      };

      const [alertsResponse, statsResponse] = await Promise.all([
        apiService.alerts.getAlerts(params),
        apiService.alerts.getAlertStats(),
      ]);

      if (!alertsResponse.data.success) {
        throw new Error(alertsResponse.data.message || "获取告警列表失败");
      }

      dispatch(setAlerts(alertsResponse.data.data?.items || []));
      setPagination((prev) => ({
        ...prev,
        total: alertsResponse.data.data?.total || 0,
      }));

      if (statsResponse.data.success) {
        dispatch(setStats(statsResponse.data.data || {}));
      }
    } catch (err) {
      message.error(err.message || "获取告警列表失败");
      dispatch(setError(err.message));
    }
  }, [dispatch, pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleViewDetail = async (record) => {
    setCurrentAlert(record);
    setShowDetailModal(true);
    
    // 如果是未读，标记为已读
    if (!record.is_read) {
      try {
        await apiService.alerts.markAsRead(record.id);
        dispatch(updateAlert({ ...record, is_read: true }));
      } catch (err) {
        console.error("Failed to mark alert as read:", err);
      }
    }
  };

  const handleAlertUpdate = (updatedAlert) => {
    dispatch(updateAlert(updatedAlert));
    loadAlerts();
  };

  const handleMarkAllRead = async () => {
    try {
      const response = await apiService.alerts.markAllAsRead();
      if (response.data.success) {
        message.success("已将所有告警标记为已读");
        loadAlerts();
      }
    } catch (err) {
      message.error(err.message || "标记失败");
    }
  };

  const handleMarkRead = async (record) => {
    try {
      const response = await apiService.alerts.markAsRead(record.id);
      if (response.data.success) {
        dispatch(updateAlert({ ...record, is_read: true }));
        message.success("已标记为已读");
      }
    } catch (err) {
      message.error(err.message || "操作失败");
    }
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: "确定要删除选中的告警吗？",
      content: `将删除 ${selectedRowKeys.length} 条记录，删除后无法恢复。`,
      okText: "删除",
      okType: "danger",
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await apiService.alerts.deleteAlertsBatch(selectedRowKeys);
          if (response.data.success) {
            message.success("删除成功");
            setSelectedRowKeys([]);
            loadAlerts();
          }
        } catch (err) {
          message.error(err.message || "删除失败");
        }
      },
    });
  };

  const handleBatchResolve = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const response = await apiService.alerts.resolveAlertsBatch(selectedRowKeys);
      if (response.data.success) {
        message.success("批量解决成功");
        setSelectedRowKeys([]);
        loadAlerts();
      }
    } catch (err) {
      message.error(err.message || "操作失败");
    }
  };

  const getSeverityTag = (severity) => {
    const config = {
      critical: { color: "red", icon: <AlertCircle size={14} />, label: "严重" },
      warning: { color: "orange", icon: <AlertTriangle size={14} />, label: "警告" },
      info: { color: "blue", icon: <Info size={14} />, label: "信息" },
    };
    const { color, icon, label } = config[severity] || config.info;
    return (
      <Tag color={color} icon={icon}>
        {label}
      </Tag>
    );
  };

  const getStatusTag = (status) => {
    const config = {
      firing: { color: "red", label: "触发中" },
      acknowledged: { color: "orange", label: "已确认" },
      resolved: { color: "green", label: "已解决" },
    };
    const { color, label } = config[status] || config.firing;
    return <Tag color={color}>{label}</Tag>;
  };

  const getTypeLabel = (type) => {
    const labels = {
      trade_volume: "交易量",
      balance: "余额",
      pnl: "盈亏",
      data_missing: "数据缺失",
    };
    return labels[type] || type;
  };

  const columns = [
    {
      title: "告警标题",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text, record) => (
        <Space>
          {!record.is_read && (
            <Tooltip title="未读">
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1890ff",
                }}
              />
            </Tooltip>
          )}
          <span style={{ fontWeight: record.is_read ? "normal" : "bold" }}>
            {text}
          </span>
        </Space>
      ),
    },
    {
      title: "规则名称",
      dataIndex: "rule_name",
      key: "rule_name",
      width: 150,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => getTypeLabel(type),
    },
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 120,
    },
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 100,
    },
    {
      title: "严重程度",
      dataIndex: "severity",
      key: "severity",
      width: 100,
      render: (severity) => getSeverityTag(severity),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: "触发时间",
      dataIndex: "fired_at",
      key: "fired_at",
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<Eye size={16} />}
              onClick={() => handleViewDetail(record)}
            >
              详情
            </Button>
          </Tooltip>
          {!record.is_read && (
            <Tooltip title="标记已读">
              <Button
                type="link"
                size="small"
                icon={<CheckCircle size={16} />}
                onClick={() => handleMarkRead(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic
              title="总告警数"
              value={stats.total || 0}
              prefix={<AlertTriangle size={20} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="触发中"
              value={stats.firing || 0}
              valueStyle={{ color: "#cf1322" }}
              prefix={<AlertCircle size={20} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已确认"
              value={stats.acknowledged || 0}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<CheckCircle size={20} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已解决"
              value={stats.resolved || 0}
              valueStyle={{ color: "#3f8600" }}
              prefix={<CheckCircle size={20} />}
            />
          </Col>
        </Row>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Select
              placeholder="告警类型"
              style={{ width: 120 }}
              allowClear
              value={filters.type || undefined}
              onChange={(value) => setFilters({ ...filters, type: value || "" })}
            >
              <Option value="trade_volume">交易量</Option>
              <Option value="balance">余额</Option>
              <Option value="pnl">盈亏</Option>
              <Option value="data_missing">数据缺失</Option>
            </Select>
            <Select
              placeholder="严重程度"
              style={{ width: 120 }}
              allowClear
              value={filters.severity || undefined}
              onChange={(value) => setFilters({ ...filters, severity: value || "" })}
            >
              <Option value="critical">严重</Option>
              <Option value="warning">警告</Option>
              <Option value="info">信息</Option>
            </Select>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => setFilters({ ...filters, status: value || "" })}
            >
              <Option value="firing">触发中</Option>
              <Option value="acknowledged">已确认</Option>
              <Option value="resolved">已解决</Option>
            </Select>
            <Select
              placeholder="阅读状态"
              style={{ width: 120 }}
              allowClear
              value={filters.is_read === null ? undefined : filters.is_read.toString()}
              onChange={(value) =>
                setFilters({
                  ...filters,
                  is_read: value === "true" ? true : value === "false" ? false : null,
                })
              }
            >
              <Option value="false">未读</Option>
              <Option value="true">已读</Option>
            </Select>
            <Button icon={<RefreshCw size={16} />} onClick={loadAlerts}>
              刷新
            </Button>
            <Button onClick={handleMarkAllRead}>
              全部标记已读
            </Button>
            <Button
              disabled={selectedRowKeys.length === 0}
              onClick={handleBatchResolve}
            >
              批量标记已解决 ({selectedRowKeys.length})
            </Button>
            <Button
              danger
              disabled={selectedRowKeys.length === 0}
              onClick={handleBatchDelete}
            >
              批量删除 ({selectedRowKeys.length})
            </Button>
          </Space>
        </div>

        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          columns={columns}
          dataSource={alerts}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
            },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="告警详情"
        open={showDetailModal}
        onCancel={() => setShowDetailModal(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <AlertDetail
          alert={currentAlert}
          onUpdate={handleAlertUpdate}
          onClose={() => setShowDetailModal(false)}
        />
      </Modal>
    </div>
  );
};

export default AlertList;
